import type { StateTree } from '@makemydeal/dr-dash-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import type { AppStateComplete } from '@makemydeal/dr-shared-store';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import * as dealModeSelectors from './dealModeSelectors';
import type { CalcEngineTransitionStateTypes } from '../types/calcEngineTransitionStateTypes';
import type { AioFSFailure } from '../types/types';

export const getActiveTab = (state: StateTree) => {
    return state.baseDeal.activeTab || DealTabs.SUMMARY;
};

export const getBreakdownStatus = (state: StateTree) => {
    return state.baseDeal.showBreakdown || false;
};

export const getVehicleInsuranceInfo = (state: StateTree) => {
    return state.baseDeal.vehicleInsuranceInfo || null;
};

export const getVehicleHasLiabilityCoverage = (state: StateTree) => {
    return getVehicleInsuranceInfo(state)?.coverage?.hasLiabilityCoverage || false;
};

export const getVehicleComplianceInfo = (state: StateTree) => {
    return state.baseDeal.vehicleComplianceInfo || null;
};

export const getVehicleInsuranceStatus = (state: StateTree) => {
    return state.baseDeal.showVehicleInsurance || false;
};

export const getCustomerRiskAndCoverageTabSlideOutStatus = (state: StateTree) => {
    return state.baseDeal.showCustomerRiskAndCoverageSlideOut || false;
};

export const getCreditBureauStatus = (state: StateTree) => {
    return state.baseDeal.showCreditBureau || false;
};

export const getBreakdownSlideoutStatus = (state: StateTree) => {
    return state.baseDeal.showBreakdownSlideout || false;
};

export const getCustomerInsightsSlideoutStatus = (state: StateTree) => {
    return state.baseDeal.showCustomerInsightsSlideout || false;
};

export const getRepairOrdersSlideoutStatus = (state: StateTree) => {
    return state.baseDeal.showRepairOrdersSlideout || false;
};

// The car the drawer was opened for, recorded because the drawer renders outside any scenario's scope
export const getRepairOrdersSlideoutVin = (state: StateTree) => {
    return state.baseDeal.repairOrdersSlideoutVin;
};

export const getActiveBreakdownTabDetails = (state: StateTree) => {
    return state.baseDeal.activeBreakdownTabDetails;
};

export const getIncentivesSlideoutTab = (state: StateTree) => {
    return state.baseDeal.incentivesSlideout.activeTab || 'included';
};

export const getCustomerRiskAndCoverageSlideoutTab = (state: StateTree) => {
    return state.baseDeal.customerRiskAndCoverageSlideout.activeTab || 'insurance';
};

export const getTradeInSlideoutTab = (state: StateTree) => {
    return state.baseDeal.tradeInSlideout?.activeTab || 'trade-in';
};

export const isDealCentralMultiScenario = (state: StateTree) => {
    return (
        dealerSelectors.isDealCentralExperience(state) &&
        scopedStateSelectors.hasMultipleActiveScopes(state as AppStateComplete<RealState, StateTree>) &&
        !dealModeSelectors.isFnIDealMode(state)
    );
};

export const isFunctionsModalVisible = (state: StateTree) => {
    return state.baseDeal.showFunctionsModal || false;
};

export const getAuditLogSlideoutStatus = (state: StateTree) => {
    return state.baseDeal.showAuditLogSlideout || false;
};

export const getSlideOutContent = (state: StateTree) => {
    return state.baseDeal.slideOutContent || null;
};

export const getBreakdownActionSource = (state: StateTree) => {
    return state.baseDeal.breakdownActionSource;
};

export const isPaymentCallPending = (state: StateTree) => {
    return state.baseDeal.paymentCallPending || false;
};

export const getInFlightPaymentCount = (state: StateTree): number => state.baseDeal.inFlightPaymentCount ?? 0;

export const isAnyMainPaymentInFlight = (state: StateTree): boolean => getInFlightPaymentCount(state) > 0;

export const getCalcEngineTransitionState = (state: StateTree): CalcEngineTransitionStateTypes => {
    return state.baseDeal.calcEngineTransitionState;
};

export const isAutoSelectProgramChecked = (state: StateTree): boolean | undefined => {
    return state.baseDeal.isAutoSelectProgramChecked;
};

export const hasBalloonFallbackOccurred = (state: StateTree): boolean => {
    return state.baseDeal.hasBalloonFallbackOccurred || false;
};

export const getAioFSFailures = (state: StateTree): AioFSFailure[] | undefined => {
    return state.baseDeal.aioDxgEvents?.fsFailures;
};

export const isVehicleInsuranceUpdated = (state: StateTree): boolean | undefined => {
    return state.baseDeal.isVehicleInsuranceUpdated;
};

export const getCurrentSlideOut = (state: StateTree): string => {
    return state.baseDeal?.currentSlideOut || 'none';
};

/** Which trade-in the appraisal slide-out was opened for; defaults to the deal's first trade-in. */
export const getAppraisalTradeInIndex = (state: StateTree): number => {
    return state.baseDeal?.appraisalTradeInIndex ?? 0;
};

export const getShowUpdateOfferForFSEvents = (state: StateTree): boolean => {
    return state.baseDeal.showUpdateOfferForFSEvents || false;
};

// Pending Navigation Selectors
export const getPendingNavigation = (state: StateTree) => state.baseDeal?.pendingNavigation || null;

export const getRightToCancelDetails = (state: StateTree) => state.baseDeal?.rightToCancelDetails || {};

export const getRightToCancelDays = (state: StateTree): number | undefined => getRightToCancelDetails(state).rightToCancelDays;

export const getRightToCancelCharge = (state: StateTree): number | undefined => getRightToCancelDetails(state).rightToCancelCharge;
